import {
  Badge,
  Box,
  chakra,
  Flex,
  HStack,
  Progress,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import { HeartHandshake } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { djs } from '@duke-hq/djs';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import { ProviderCauseStatusSchema } from '@duke-hq/svc-api-db/schemas/enums/ProviderCauseStatus.schema';
import { ContributorsLeaderboardWidget } from './Contributors/ContributorsLeaderboardWidget';
import { SiteLeaderboardWidget } from './Sites/SiteLeaderboardWidget';
import { CauseCard } from './CauseCard';
import { CauseFormModal } from './CauseFormModal';

type UserTier = RouterOutputs['groupRewards']['getUserIncentivePoints']['userTier'];

type EffortPointsCardProps = {
  userPoints: RouterOutputs['groupRewards']['getUserIncentivePoints'];
  userActivity: RouterOutputs['users']['getUserConsecutiveDailyStreak'] | undefined;
};

function calculateDaysUntilPlatinum(userTier: UserTier | null): {
  daysRemaining: number;
  progressPercentage: number;
  isAlreadyPlatinum: boolean;
} {
  if (!userTier) {
    return {
      daysRemaining: 0,
      progressPercentage: 0,
      isAlreadyPlatinum: false,
    };
  }

  const { streakWeeks, streakWeeksAssignedAt, tier } = userTier;
  const PLATINUM_REQUIREMENT = 4;

  // Already Platinum
  if (tier.name === 'Platinum' || streakWeeks >= PLATINUM_REQUIREMENT) {
    return {
      daysRemaining: 0,
      progressPercentage: 100,
      isAlreadyPlatinum: true,
    };
  }

  // Calculate weeks needed and convert to days
  const weeksNeeded = PLATINUM_REQUIREMENT - streakWeeks;

  // For users with 1 streak week (first-time login)
  if (streakWeeks === 1) {
    const lastUpdateDate = new Date(streakWeeksAssignedAt);
    const now = new Date();

    const daysSinceLastUpdate = Math.floor(
      (now.getTime() - lastUpdateDate.getTime()) / (1000 * 60 * 60 * 24),
    );

    // User needs 3 more complete weeks (21 days) plus remaining days in current week
    const daysInWeek = 7;
    const daysRemainingInCurrentWeek = Math.max(0, daysInWeek - (daysSinceLastUpdate % daysInWeek));
    const additionalWeeksNeeded = 3; // Need 3 more weeks after the first

    const totalDaysRemaining = daysRemainingInCurrentWeek + additionalWeeksNeeded * daysInWeek;
    const totalDaysNeeded = PLATINUM_REQUIREMENT * 7; // 28 days
    const daysCompleted = totalDaysNeeded - totalDaysRemaining;
    const progressPercentage = (daysCompleted / totalDaysNeeded) * 100;

    return {
      daysRemaining: totalDaysRemaining,
      progressPercentage: Math.max(0, progressPercentage),
      isAlreadyPlatinum: false,
    };
  }

  const lastUpdateDate = new Date(streakWeeksAssignedAt);
  const now = new Date();

  // Calculate days since last streak update
  const daysSinceLastUpdate = Math.floor(
    (now.getTime() - lastUpdateDate.getTime()) / (1000 * 60 * 60 * 24),
  );

  const daysInWeek = 7;
  const daysRemainingInCurrentWeek = Math.max(0, daysInWeek - (daysSinceLastUpdate % daysInWeek));
  const additionalWeeksNeeded = Math.max(0, weeksNeeded - 1);

  const totalDaysRemaining = daysRemainingInCurrentWeek + additionalWeeksNeeded * daysInWeek;
  const totalDaysNeeded = PLATINUM_REQUIREMENT * 7; // 28 days
  const daysCompleted = totalDaysNeeded - totalDaysRemaining;
  const progressPercentage = (daysCompleted / totalDaysNeeded) * 100;

  return {
    daysRemaining: totalDaysRemaining,
    progressPercentage: Math.min(Math.max(0, progressPercentage), 100),
    isAlreadyPlatinum: false,
  };
}

function HexagonIconBadge() {
  return (
    <Box boxSize="12" position="relative">
      <chakra.svg
        viewBox="0 0 100 100"
        width="100%"
        height="100%"
        position="absolute"
        top="0"
        left="0"
      >
        <polygon points="50,0 93,25 93,75 50,100 7,75 7,25" fill="white" />
        <polygon points="50,2 91,26 91,74 50,98 9,74 9,26" fill="black" />
        <polygon points="50,4 89,27 89,73 50,96 11,73 11,27" fill="white" />
        <polygon points="50,6 87,28 87,72 50,94 13,72 13,28" fill="#7865E0" />
      </chakra.svg>

      <Box
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        color="white"
        fontSize="2xl"
      >
        <HeartHandshake />
      </Box>
    </Box>
  );
}

function EffortPointsCard({ userPoints, userActivity }: EffortPointsCardProps): React.ReactElement {
  const monthlyPoints = userPoints.totalPoints;
  const allPoints = userPoints.allTotalPoints;
  const { daysRemaining, progressPercentage, isAlreadyPlatinum } = calculateDaysUntilPlatinum(
    userPoints.userTier,
  );

  const isPlatinum = userPoints.userTier?.tier.name === 'Platinum' || isAlreadyPlatinum;
  const cardGradient = isPlatinum
    ? 'linear(to-r, gray.400, gray.300)'
    : 'linear(to-r, purple.500, purple.400)';
  const progressColorScheme = isPlatinum ? 'gray' : 'purple';

  return (
    <VStack align="stretch" width="full" gap={2.5}>
      <Box>
        <Text fontSize="xl" fontWeight="semibold" color="fg.muted" whiteSpace="nowrap">
          <Trans>Your points</Trans>
        </Text>
      </Box>
      <Box borderRadius="2xl" overflow="hidden" boxShadow="md" width="100%">
        <Box bgGradient={cardGradient} px={6} py={4} color="white">
          <Flex justify="space-between" align="flex-start">
            <HStack spacing={4}>
              <Box display="flex" alignItems="center" justifyContent="center">
                <HexagonIconBadge />
              </Box>
              <VStack align="start" spacing={0}>
                <Text fontWeight="semibold" fontSize="lg">
                  <Trans>Good Effort Points</Trans>
                </Text>
                <Badge colorScheme="whiteAlpha" variant="subtle" fontSize="sm">
                  {userPoints.userTier?.tier.name != null
                    ? userPoints.userTier?.tier.name
                    : 'Standard'}{' '}
                  Partner
                </Badge>
              </VStack>
            </HStack>

            <HStack spacing={2} align="flex-end">
              <HStack spacing={4} align="center">
                <VStack spacing={0} align="end" lineHeight="1">
                  <Text fontSize="xs" fontWeight="semibold">
                    <Trans>THIS</Trans>
                  </Text>
                  <Text fontSize="xs" fontWeight="semibold">
                    <Trans>MONTH</Trans>
                  </Text>
                </VStack>
                <Text fontSize="3xl" fontWeight="bold" lineHeight="short">
                  {monthlyPoints}
                </Text>
              </HStack>

              <Box width="1px" height="32px" backgroundColor="bg.canvas" mx={1} />

              <HStack spacing={4} align="center">
                <VStack spacing={0} align="end" lineHeight="1">
                  <Text fontSize="xs" fontWeight="semibold">
                    <Trans>ALL</Trans>
                  </Text>
                  <Text fontSize="xs" fontWeight="semibold">
                    <Trans>TIME</Trans>
                  </Text>
                </VStack>
                <Text fontSize="3xl" fontWeight="bold" lineHeight="short">
                  {allPoints}
                </Text>
              </HStack>
            </HStack>
          </Flex>
        </Box>

        <Box bg="white" px={6} py={4}>
          <Text fontSize="sm" fontWeight="medium" mb={2}>
            {isAlreadyPlatinum ? (
              <Trans>Congratulations! You're a Platinum Partner</Trans>
            ) : (
              <Trans>{daysRemaining} more days to Platinum Partner</Trans>
            )}
          </Text>
          <Progress
            value={progressPercentage}
            size="sm"
            colorScheme={progressColorScheme}
            borderRadius="md"
            mb={2}
          />
          <Text fontSize="xs" color="fg.muted">
            <Trans>
              You've hit your monitoring goals for {userActivity?.length ?? 0} days straight.
            </Trans>
          </Text>
        </Box>
      </Box>
    </VStack>
  );
}

function RecentContributionsCard(): React.ReactElement {
  const { data: recentContributions } = trpcTenant.groupRewards.getRecentContributions.useQuery({
    limit: 3,
  });

  return (
    <VStack gap={2.5} alignItems="start">
      <Box>
        <Text fontSize="xl" fontWeight="semibold" color="fg.muted" whiteSpace="nowrap">
          <Trans>Your recent contributions</Trans>
        </Text>
      </Box>
      <Box bg="bg.surface" borderRadius="2xl" py={4} width="100%">
        {recentContributions != null && recentContributions.length === 0 ? (
          <Text fontSize="md" color="fg.muted">
            <Trans>No recent contributions found.</Trans>
          </Text>
        ) : (
          <VStack spacing="10px">
            {recentContributions?.map((contribution) => (
              <VStack
                key={contribution.id}
                w="full"
                spacing={1}
                align="stretch"
                gap={0}
                whiteSpace="nowrap"
              >
                <HStack spacing={2} justify="space-between" width="full" alignItems="start">
                  <Text fontSize="sm" fontWeight="semibold" color="fg.emphasis">
                    {contribution.cause.name}
                  </Text>
                  <Text fontSize="md" fontWeight="medium" color="green.600">
                    +${contribution.pointsAllocated}
                  </Text>
                </HStack>
                <HStack spacing={2} alignItems="center" width="full" justifyContent="space-between">
                  <Text color="fg.muted" fontSize="xs" fontWeight="medium">
                    {djs(contribution.createdAt).format('MMM DD, YYYY')}
                  </Text>
                  <Text fontSize="xs" fontWeight="medium" color="green.600">
                    <Trans>Your total contribution</Trans> $
                    {contribution.totalPointsContributedToCause}
                  </Text>
                </HStack>
              </VStack>
            ))}
          </VStack>
        )}
      </Box>
    </VStack>
  );
}

export function GroupRewardsCauses(): React.ReactElement {
  const { data: userPoints, isPending: isUserPointsPending } =
    trpcTenant.groupRewards.getUserIncentivePoints.useQuery();
  const { data: userActivity } = trpcTenant.users.getUserConsecutiveDailyStreak.useQuery({});
  const { data: causes } = trpcTenant.groupRewards.listCauses.useQuery({
    filters: {
      status: ProviderCauseStatusSchema.enum.APPROVED,
    },
  });

  return (
    <SimpleGrid templateColumns={{ base: '1fr', lg: '3fr 1fr' }} spacing={6} alignItems="start">
      <Box w="full" h="full" overflow="auto">
        {isUserPointsPending ? (
          <Text fontSize="md" color="fg.muted">
            <Trans>Loading your points...</Trans>
          </Text>
        ) : userPoints ? (
          <SimpleGrid
            templateColumns={{ base: '1fr', lg: '3fr 1fr' }}
            spacing={6}
            alignItems="start"
          >
            <EffortPointsCard userPoints={userPoints} userActivity={userActivity} />
            <Box gridColumn={{ base: '1', lg: 'auto' }}>
              <RecentContributionsCard />
            </Box>
          </SimpleGrid>
        ) : (
          <Text fontSize="md" color="fg.muted">
            <Trans>Unable to load your points.</Trans>
          </Text>
        )}

        <Flex justify="space-between" alignItems="center" my={4}>
          <Flex justify="start">
            <VStack align="stretch" width="full" gap={1}>
              <Text fontSize="xl" fontWeight="semibold" color="fg.muted">
                <Trans>Contribute to a cause</Trans>
              </Text>
              <Text fontSize="md" fontWeight="medium" color="fg.muted">
                <Trans>
                  Contribute to a cause below, or suggest one that is close to your heart.
                </Trans>
              </Text>
            </VStack>
          </Flex>
          <Flex justify="end" alignItems="center" gap="8px">
            <CauseFormModal key={'causeFormModal'} />
          </Flex>
        </Flex>

        <Box p={4} bg="bg.subtle" height="auto" borderRadius="md" boxShadow="sm" overflowY="auto">
          {causes != null && Array.isArray(causes) && causes.length === 0 ? (
            <Text fontSize="md" color="fg.muted">
              <Trans>No causes found.</Trans>
            </Text>
          ) : (
            <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
              {(Array.isArray(causes) ? causes : []).map(
                (cause: RouterOutputs['groupRewards']['listCauses'][number]) => (
                  <CauseCard key={cause.id} cause={cause} />
                ),
              )}
            </SimpleGrid>
          )}
        </Box>
      </Box>
      <Flex flexDirection="column" gap={6}>
        <ContributorsLeaderboardWidget />
        <SiteLeaderboardWidget />
      </Flex>
    </SimpleGrid>
  );
}
